// Steuerpaket S2: Anlage-V-Aufstellung eines Objekts aus dem Immoware-Journal nach Björns Regeln (26.10.2026).
//  1 Ist-Prinzip: es zählt nur, was tatsächlich gezahlt wurde (Bankbewegungen). Rechnungen ohne Zahlung,
//    Abgrenzungen und Durchlaufposten bleiben draußen.
//  2 Zahlungsdatum; regelmäßige Zahlungen (Mieten, Hausgeld, Darlehensraten) bis 10 Tage nach Jahresende
//    zählen zum alten Jahr, wenn sie dorthin gehören (und umgekehrt kurz vor Jahresende fürs neue Jahr).
//  3 Darlehen: nur Zinsen (aus dem Buchungstext „ZINS EUR …“), keine Tilgung.
//  4 Umbuchungen/Korrekturen zwischen Erfolgskonten zählen mit.
//  5 WEG: Rücklagenzuführung zählt nicht (sie ist keine Ausgabe der WEG); verteilt wird nach Schlüsseln (weg.ts).
//  6 PV-Einnahmen, Kautionen, Auszahlungen an den Eigentümer: nicht in der Anlage V.
//  Was nicht eindeutig zuzuordnen ist, wird „ungeklärt“ markiert – das Büro klärt es (Klaerung), es wird nicht geraten.
import { BANK_KATEGORIEN, type JournalZeile } from './immowareJournal';

export type Buchung = Omit<JournalZeile, 'zeile'> & { zeile?: number };

export type Kategorie =
  | 'miete' | 'umlagen' | 'stellplatz' | 'sonstige_einnahmen'
  | 'schuldzinsen' | 'erhaltung' | 'laufend' | 'verwaltung' | 'sonstige_wk';

export const KATEGORIE_TEXT: Record<Kategorie, string> = {
  miete: 'Mieteinnahmen (ohne Umlagen)',
  umlagen: 'Umlagen / Nebenkosten-Vorauszahlungen',
  stellplatz: 'Garagen und Stellplätze',
  sonstige_einnahmen: 'Sonstige Einnahmen',
  schuldzinsen: 'Schuldzinsen',
  erhaltung: 'Erhaltungsaufwendungen',
  laufend: 'Grundsteuer, Müll, Wasser, Energie, Versicherungen, Hauswart u. Ä.',
  verwaltung: 'Verwaltungskosten',
  sonstige_wk: 'Sonstige Werbungskosten',
};
export const KATEGORIE_KURZ: Record<Kategorie, string> = {
  miete: 'Miete', umlagen: 'Umlagen', stellplatz: 'Stellplatz', sonstige_einnahmen: 'Sonst. Einnahme',
  schuldzinsen: 'Schuldzinsen', erhaltung: 'Erhaltung', laufend: 'Laufende Kosten', verwaltung: 'Verwaltung', sonstige_wk: 'Sonst. Kosten',
};
export const EINNAHME_KATEGORIEN: readonly Kategorie[] = ['miete', 'umlagen', 'stellplatz', 'sonstige_einnahmen'];
export const KOSTEN_KATEGORIEN: readonly Kategorie[] = ['schuldzinsen', 'erhaltung', 'laufend', 'verwaltung', 'sonstige_wk'];

export type NichtAnlageV = 'pv' | 'kaution' | 'tilgung' | 'darlehen_auszahlung' | 'eigentuemer' | 'hausgeld' | 'ruecklage' | 'abgrenzung';
export const NICHT_ANLAGE_V_TEXT: Record<NichtAnlageV, string> = {
  pv: 'PV-Einnahmen (eigene Einkunftsart)',
  kaution: 'Kautionen',
  tilgung: 'Darlehenstilgung',
  darlehen_auszahlung: 'Darlehensauszahlung',
  eigentuemer: 'Auszahlungen an / Einzahlungen vom Eigentümer',
  hausgeld: 'Hausgeldzahlungen der Eigentümer an die WEG',
  ruecklage: 'Rücklagen-Bewegungen',
  abgrenzung: 'Abgrenzungen und Durchlaufposten (keine Zahlung)',
};

/** Entscheidung des Büros zu einer ungeklärten Buchung (Tabelle buchhaltung_klaerungen). */
export interface Klaerung {
  schluessel: string;
  aktion: 'zuordnen' | 'ausschliessen';
  kategorie?: Kategorie | null;
  /** Steuerjahr, falls abweichend vom Zahlungsjahr */
  jahr?: number | null;
  /** nur Darlehen: Zinsanteil der Zahlung */
  zins?: number | null;
}

export interface Posten { kategorie: Kategorie; konto: string; betrag: number; buchungen: string[] }
export interface Ungeklaert { schluessel: string; grund: string; buchung: Buchung; betrag: number; vorschlag?: Kategorie }
export interface ObjektErgebnis {
  jahr: number;
  posten: Posten[];
  summen: Record<Kategorie, number>;
  einnahmen: number;
  werbungskosten: number;
  ueberschuss: number;
  nichtAnlageV: Record<NichtAnlageV, number>;
  ungeklaert: Ungeklaert[];
  /** Rechnungen ohne Zahlung im Jahr (nur Hinweis, nicht gezählt) */
  offeneRechnungen: { konto: string; betrag: number; buchung: Buchung }[];
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const d = (iso: string) => new Date(iso + 'T12:00:00Z');
const jahrVon = (iso: string | null) => (iso ? Number(iso.slice(0, 4)) : NaN);

/** Stabiler Schlüssel einer Buchungszeile – übersteht einen erneuten Import desselben Journals. */
export function buchungsSchluessel(b: Buchung): string {
  return [b.nummer, b.wertstellung, b.abgrenzung, b.brutto.toFixed(2), b.soll_konto_nr, b.haben_konto_nr].join('|');
}

/** Konto → Kategorie nach Name (Immoware-Kontonummern sind je Objektart unterschiedlich belegt). */
export function kategorieFuerKonto(name: string, art: string): Kategorie | NichtAnlageV | null {
  const n = name.toLowerCase();
  if (art === 'Ertrag') {
    if (/\bpv\b|photovolt|einspeis/.test(n)) return 'pv';
    if (/hausgeld|rücklage|ruecklage/.test(n)) return 'hausgeld';
    if (/stellplatz|garage|carport/.test(n)) return 'stellplatz';
    if (/betriebskost|nebenkost|heizkost|umlage|-vz\b|vorauszahl|nachzahl/.test(n)) return 'umlagen';
    if (/miete/.test(n)) return 'miete';
    return 'sonstige_einnahmen';
  }
  if (art === 'Kosten') {
    if (/instandhalt|instandsetz|reparatur|erhaltung|sanierung/.test(n)) return 'erhaltung';
    if (/verwalter|verwaltung|hausgeldabrechnung/.test(n)) return 'verwaltung';
    if (/geldverkehr|kontoführ|bankgeb|rechts|beratung|mahn|porto/.test(n)) return 'sonstige_wk';
    return 'laufend';
  }
  return null;
}

type Richtung = { gegen: 'soll' | 'haben'; vorzeichen: 1 | -1 };
/** Bankbuchung? Liefert die Gegenseite und ob Geld rein (+1) oder raus (−1) ging. */
function bankRichtung(b: Buchung): Richtung | null {
  const sb = BANK_KATEGORIEN.has(b.soll_kategorie); const hb = BANK_KATEGORIEN.has(b.haben_kategorie);
  if (sb && hb) return null;              // Umbuchung zwischen eigenen Konten
  if (sb) return { gegen: 'haben', vorzeichen: 1 };
  if (hb) return { gegen: 'soll', vorzeichen: -1 };
  return null;
}
const seite = (b: Buchung, s: 'soll' | 'haben') => ({
  nr: s === 'soll' ? b.soll_konto_nr : b.haben_konto_nr,
  name: s === 'soll' ? b.soll_konto : b.haben_konto,
  art: s === 'soll' ? b.soll_kontoart : b.haben_kontoart,
  kat: s === 'soll' ? b.soll_kategorie : b.haben_kategorie,
});

/**
 * 10-Tage-Regel (Björn, Regel 2): Zahlung bis 10.01. für einen Zeitraum im Dezember des Vorjahres → Vorjahr;
 * Zahlung ab 22.12. für einen Zeitraum im Januar des Folgejahres → Folgejahr. Sonst zählt das Zahlungsjahr.
 */
export function steuerjahr(zahlung: string, bezug: string | null): number {
  const z = d(zahlung); const j = z.getUTCFullYear();
  if (bezug) {
    const b = d(bezug);
    if (z.getUTCMonth() === 0 && z.getUTCDate() <= 10 && b.getUTCFullYear() === j - 1 && b.getUTCMonth() === 11) return j - 1;
    if (z.getUTCMonth() === 11 && z.getUTCDate() >= 22 && b.getUTCFullYear() === j + 1 && b.getUTCMonth() === 0) return j + 1;
  }
  return j;
}

const darlehenKonto = (name: string, art: string) => art === 'passiv' && /darlehen|hypothek|kredit|grundschuld/i.test(name);
const kautionKonto = (name: string) => /kaution/i.test(name);
const eigentuemerKonto = (name: string) => /auszahlung an eigent|eigentümer|einlage|entnahme|privat/i.test(name);
const ruecklageKonto = (name: string) => /rücklage|ruecklage/i.test(name);
const deZahl = (s: string) => Number(s.replace(/\./g, '').replace(',', '.'));

/** Zinsanteil und Bezugsdatum aus dem Buchungstext der Bank („PER 31.12.24 / ZINS EUR 2.216,08“). */
export function darlehenAusText(text: string): { zins: number | null; bezug: string | null } {
  const z = text.match(/ZINS\s+EUR\s+([\d.]+,\d{2})/i);
  const p = text.match(/PER\s+(\d{2})\.(\d{2})\.(\d{2,4})/i);
  const jahr = p ? (p[3].length === 2 ? '20' + p[3] : p[3]) : null;
  return { zins: z ? deZahl(z[1]) : null, bezug: p ? `${jahr}-${p[2]}-${p[1]}` : null };
}

export function berechneObjekt(buchungen: readonly Buchung[], jahr: number, klaerungen: readonly Klaerung[] = []): ObjektErgebnis {
  const klaer = new Map(klaerungen.map((k) => [k.schluessel, k]));
  const posten = new Map<string, Posten>();
  const nicht = Object.fromEntries(Object.keys(NICHT_ANLAGE_V_TEXT).map((k) => [k, 0])) as Record<NichtAnlageV, number>;
  const ungeklaert: Ungeklaert[] = [];
  const buche = (kat: Kategorie, konto: string, betrag: number, sl: string) => {
    const key = kat + '|' + konto;
    const p = posten.get(key) ?? { kategorie: kat, konto, betrag: 0, buchungen: [] };
    p.betrag += betrag; p.buchungen.push(sl); posten.set(key, p);
  };
  /** Ungeklärt – außer das Büro hat entschieden; dann gilt die Entscheidung. */
  const offen = (b: Buchung, betrag: number, grund: string, jahrVorschlag: number, vorschlag?: Kategorie) => {
    const sl = buchungsSchluessel(b);
    const k = klaer.get(sl);
    if (k) {
      if (k.aktion === 'ausschliessen' || (k.jahr ?? jahrVorschlag) !== jahr) return;
      const kat = k.kategorie ?? vorschlag;
      if (kat) {
        // betrag ist aus Sicht der Bank (Ausgang negativ); Werbungskosten werden positiv geführt
        const wert = kat === 'schuldzinsen' && k.zins != null ? Math.abs(k.zins) : (KOSTEN_KATEGORIEN as string[]).includes(kat) ? -betrag : betrag;
        buche(kat, kat === 'schuldzinsen' ? 'Schuldzinsen' : 'Geklärt durch das Büro', wert, sl);
        return;
      }
    }
    if (jahrVorschlag === jahr) ungeklaert.push({ schluessel: sl, grund, buchung: b, betrag: r2(betrag), vorschlag });
  };

  // Sollstellungen je Mieter-/Eigentümerkonto und Monat → Aufteilung der Zahlungen auf Miete/Umlagen/Stellplatz
  const soll = new Map<string, Map<string, { betrag: number; art: string }>>();
  for (const b of buchungen) {
    if (b.buchungstyp !== 'Sollstellung' || !b.abgrenzung) continue;
    const key = `${b.soll_konto_nr}|${b.abgrenzung.slice(0, 7)}`;
    const m = soll.get(key) ?? new Map(); const e = m.get(b.haben_konto) ?? { betrag: 0, art: b.haben_kontoart };
    e.betrag += b.brutto; m.set(b.haben_konto, e); soll.set(key, m);
  }

  // Rechnungen je Kreditor; Zahlungen werden über alle Jahre zugeordnet (gleicher Betrag, gleiche Abgrenzung bevorzugt)
  const rechnungen = buchungen.filter((b) => b.buchungstyp === 'Rechnung');
  const bezahlt = new Set<Buchung>();
  const rechnungZu = new Map<Buchung, Buchung>();
  for (const z of buchungen.filter((b) => b.buchungstyp === 'Zahlung Kreditor' && bankRichtung(b))) {
    const r = bankRichtung(z)!; const kred = seite(z, r.gegen).nr; const betrag = z.brutto;
    const kand = rechnungen.filter((x) => !bezahlt.has(x) && x.haben_konto_nr === kred && Math.abs(x.brutto - betrag) < 0.005);
    kand.sort((a, b) => Number(a.abgrenzung !== z.abgrenzung) - Number(b.abgrenzung !== z.abgrenzung)
      || Math.abs(d(a.wertstellung!).getTime() - d(z.wertstellung!).getTime()) - Math.abs(d(b.wertstellung!).getTime() - d(z.wertstellung!).getTime()));
    if (kand[0]) { bezahlt.add(kand[0]); rechnungZu.set(z, kand[0]); }
  }

  for (const b of buchungen) {
    const r = bankRichtung(b);
    if (!r || !b.wertstellung) continue;
    const g = seite(b, r.gegen); const betrag = b.brutto * r.vorzeichen; const sl = buchungsSchluessel(b);
    const zj = jahrVon(b.wertstellung);

    if (g.kat === 'Mieter' || g.kat === 'Eigentümer') {
      const sj = steuerjahr(b.wertstellung, b.abgrenzung);
      if (g.kat === 'Eigentümer') { if (sj === jahr) nicht.hausgeld += betrag; continue; }
      const anteile = soll.get(`${g.nr}|${(b.abgrenzung ?? '').slice(0, 7)}`);
      const summe = anteile ? [...anteile.values()].reduce((s, e) => s + e.betrag, 0) : 0;
      if (!anteile || Math.abs(summe) < 0.005) {
        offen(b, betrag, 'Mieterzahlung ohne passende Sollstellung', sj, 'miete'); continue;
      }
      if (sj !== jahr) continue;
      for (const [konto, e] of anteile) {
        const kat = kategorieFuerKonto(konto, e.art);
        const teil = (betrag * e.betrag) / summe;
        if (kat === 'pv' || kat === 'hausgeld') nicht[kat === 'pv' ? 'pv' : 'hausgeld'] += teil;
        else if (kat && (EINNAHME_KATEGORIEN as string[]).includes(kat)) buche(kat as Kategorie, konto, teil, sl);
        else buche('sonstige_einnahmen', konto, teil, sl);
      }
      continue;
    }

    if (g.kat === 'Kreditoren') {
      const rech = rechnungZu.get(b);
      if (!rech) { offen(b, betrag, 'Zahlung an Kreditor ohne passende Rechnung', zj); continue; }
      if (zj !== jahr) continue;
      if (eigentuemerKonto(rech.soll_konto)) { nicht.eigentuemer += betrag; continue; }
      const kat = kategorieFuerKonto(rech.soll_konto, rech.soll_kontoart);
      if (kat && (KOSTEN_KATEGORIEN as string[]).includes(kat)) buche(kat as Kategorie, rech.soll_konto, -betrag, sl);
      else offen(b, betrag, `Rechnung auf Konto „${rech.soll_konto}“`, zj);
      continue;
    }

    if (darlehenKonto(g.name, g.art)) {
      const { zins, bezug } = darlehenAusText(b.text);
      const sj = steuerjahr(b.wertstellung, bezug);
      if (r.vorzeichen > 0) { if (sj === jahr) nicht.darlehen_auszahlung += betrag; continue; }
      const k = klaer.get(sl);
      const z = k?.zins ?? zins;
      if (k?.aktion === 'ausschliessen') continue;
      if (z == null) { offen(b, betrag, 'Darlehenszahlung ohne Zinsangabe im Text', sj, 'schuldzinsen'); continue; }
      if ((k?.jahr ?? sj) !== jahr) continue;
      buche('schuldzinsen', 'Schuldzinsen', z, sl);
      nicht.tilgung += -betrag - z;
      continue;
    }

    if (g.art === 'Kosten' || g.art === 'Ertrag') {
      if (zj !== jahr) continue;
      const kat = kategorieFuerKonto(g.name, g.art);
      if (kat === 'pv') nicht.pv += betrag;
      else if (kat === 'hausgeld') nicht.ruecklage += betrag;
      else if (kat) buche(kat as Kategorie, g.name, g.art === 'Kosten' ? -betrag : betrag, sl);
      continue;
    }
    if (kautionKonto(g.name)) { if (zj === jahr) nicht.kaution += betrag; continue; }
    if (ruecklageKonto(g.name)) { if (zj === jahr) nicht.ruecklage += betrag; continue; }
    if (eigentuemerKonto(g.name)) { if (zj === jahr) nicht.eigentuemer += betrag; continue; }
    offen(b, betrag, `Gegenkonto „${g.name}“ nicht zuzuordnen`, zj);
  }

  // Umbuchungen/Korrekturen ohne Bank (Regel 4): nur zwischen Erfolgskonten. Jahr = Zahlungsjahr der Korrektur,
  // es sei denn, die Wertstellung ist nur das Buchungsdatum und die Abgrenzung liegt in einem anderen Jahr → ungeklärt.
  for (const b of buchungen) {
    if (bankRichtung(b) || BANK_KATEGORIEN.has(b.soll_kategorie) || BANK_KATEGORIEN.has(b.haben_kategorie)) continue;
    if (b.buchungstyp === 'Sollstellung' || b.buchungstyp === 'Rechnung' || b.buchungstyp === 'Anfangsbestandsbuchung') continue;
    if (!b.wertstellung) continue;
    const s = seite(b, 'soll'); const h = seite(b, 'haben');
    const erfolg = (a: string) => a === 'Kosten' || a === 'Ertrag';
    if (!erfolg(s.art) || !erfolg(h.art)) {
      const j = jahrVon(b.abgrenzung ?? b.wertstellung);
      if (j === jahr && (erfolg(s.art) || erfolg(h.art))) nicht.abgrenzung += b.brutto;
      continue;
    }
    const wj = jahrVon(b.wertstellung); const aj = jahrVon(b.abgrenzung);
    const eindeutig = wj === aj || b.wertstellung !== b.buchungsdatum;
    if (!eindeutig) {
      const k = klaer.get(buchungsSchluessel(b));
      if (!k) { if (aj === jahr || wj === jahr) ungeklaert.push({ schluessel: buchungsSchluessel(b), grund: `Korrektur gebucht ${b.buchungsdatum?.slice(0, 4)}, betrifft ${aj} – welches Jahr?`, buchung: b, betrag: b.brutto }); continue; }
      if (k.aktion === 'ausschliessen' || (k.jahr ?? aj) !== jahr) continue;
    } else if (wj !== jahr) continue;
    const sl = buchungsSchluessel(b);
    for (const [x, v] of [[s, 1], [h, -1]] as const) {
      const kat = kategorieFuerKonto(x.name, x.art);
      if (kat === 'pv') nicht.pv -= v * b.brutto;
      else if (kat === 'hausgeld') nicht.hausgeld -= v * b.brutto;
      else if (kat) buche(kat as Kategorie, x.name, x.art === 'Kosten' ? v * b.brutto : -v * b.brutto, sl);
    }
  }

  // Rechnung + Gutschrift/Storno über denselben Betrag beim selben Kreditor heben sich auf
  const unbezahlt = rechnungen.filter((x) => !bezahlt.has(x) && Math.abs(x.brutto) >= 0.005);
  const storniert = new Set<Buchung>();
  for (const x of unbezahlt) {
    if (storniert.has(x)) continue;
    const g = unbezahlt.find((y) => y !== x && !storniert.has(y) && y.haben_konto_nr === x.haben_konto_nr && Math.abs(y.brutto + x.brutto) < 0.005);
    if (g) { storniert.add(x); storniert.add(g); }
  }
  const offeneRechnungen = unbezahlt.filter((x) => !storniert.has(x) && jahrVon(x.abgrenzung) === jahr)
    .map((x) => ({ konto: x.soll_konto, betrag: x.brutto, buchung: x }));

  const liste = [...posten.values()].map((p) => ({ ...p, betrag: r2(p.betrag) })).filter((p) => Math.abs(p.betrag) >= 0.005)
    .sort((a, b) => a.kategorie.localeCompare(b.kategorie) || a.konto.localeCompare(b.konto));
  const summen = Object.fromEntries(Object.keys(KATEGORIE_TEXT).map((k) => [k, r2(liste.filter((p) => p.kategorie === k).reduce((s, p) => s + p.betrag, 0))])) as Record<Kategorie, number>;
  const einnahmen = r2(EINNAHME_KATEGORIEN.reduce((s, k) => s + summen[k], 0));
  const werbungskosten = r2(KOSTEN_KATEGORIEN.reduce((s, k) => s + summen[k], 0));
  for (const k of Object.keys(nicht) as NichtAnlageV[]) nicht[k] = r2(nicht[k]);
  return { jahr, posten: liste, summen, einnahmen, werbungskosten, ueberschuss: r2(einnahmen - werbungskosten), nichtAnlageV: nicht, ungeklaert, offeneRechnungen };
}
